import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { LanguageMenu } from './LanguageMenu';
import type { Locale } from '../types/locale';
import { useLanguage } from '../contexts/LanguageContext';
import { useSuggestedLocale } from '../hooks/useSuggestedLocale';
import { languageBannerStorageKey } from '../data/locales';
import { localeMeta } from '../utils/locale';
const ease = [0.22, 1, 0.36, 1] as const;

// Apple-style suggestion above everything: offered only when we speak the visitor's local language,
// and written in that language
export function LanguageBanner() {
  const {
    locale,
    switchLocale
  } = useLanguage();
  const {
    pathname
  } = useLocation();
  const suggestion = useSuggestedLocale();
  const suggested = suggestion?.locale ?? null;
  const [dismissed, setDismissed] = useState(wasDismissed);
  const [choice, setChoice] = useState<Locale | null>(null);
  const visible = !!suggested && suggested !== locale && !dismissed && pathname !== '/venturist';
  const copy = localeMeta(suggested ?? 'en');
  const selected = choice ?? suggested ?? locale;
  const prompt = copy.prompt.replace('{country}', countryName(suggestion?.country ?? '', copy.code));
  const close = () => {
    rememberDismissal();
    setDismissed(true);
  };
  const onContinue = () => {
    rememberDismissal();
    if (selected === locale) setDismissed(true);else switchLocale(selected);
  };
  return <AnimatePresence initial={false} data-id="element-627" data-mp="2bszider17mfr">
      {visible && <motion.div key="language-banner" initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} transition={{
      duration: 0.5,
      ease
    }} className="relative z-[60] border-b border-line bg-bone text-ink" role="region" aria-label={prompt} lang={copy.code} dir={copy.dir} data-id="element-628" data-mp="1xuszp5unlt0o">
          <div className="mx-auto flex max-w-[1360px] flex-col gap-4 px-6 py-4 md:flex-row md:items-center md:gap-10 md:px-14" data-id="element-629" data-mp="2t2bggvzmmcxc">
            <p className="max-w-[560px] pe-10 text-[13px] font-light leading-[20px] text-ink/75 md:pe-0" data-id="element-630" data-mp="2o6mpvi5l46d6">{prompt}</p>

            <div className="flex items-center gap-4 md:ms-auto md:pe-10" data-id="element-631" data-mp="2jbgwxzdrlrfl">
              <LanguageMenu value={selected} onChange={setChoice} label={prompt} className="min-w-0 flex-1 md:w-[280px] md:flex-none" data-id="element-632" data-mp="2eego5gpj6aks" />
              <button type="button" onClick={onContinue} className="shrink-0 whitespace-nowrap bg-ink px-5 py-2.5 text-[14px] font-medium text-paper transition-opacity duration-150 hover:opacity-80" data-id="element-633" data-mp="25ebs22pseylx">
                {copy.continueLabel}
              </button>
            </div>
          </div>

          <button type="button" onClick={close} aria-label={copy.closeLabel} className="absolute end-4 top-4 p-1 text-ink/45 transition-colors duration-150 hover:text-ink md:end-8 md:top-1/2 md:-translate-y-1/2" data-id="element-634" data-mp="3usgmwocn3u6g">
            <XIcon className="h-4 w-4" strokeWidth={1.25} data-id="element-635" data-mp="6621rhuremsm" />
          </button>
        </motion.div>}
    </AnimatePresence>;
}

// The country's name in the banner's own language, e.g. "Deutschland", "Émirats arabes unis", "الإمارات"
function countryName(code: string, locale: Locale) {
  if (!code) return '';
  try {
    return new Intl.DisplayNames([locale], {
      type: 'region'
    }).of(code) ?? code;
  } catch {
    return code;
  }
}
function wasDismissed() {
  try {
    return window.localStorage.getItem(languageBannerStorageKey) === 'dismissed';
  } catch {
    return false;
  }
}
function rememberDismissal() {
  try {
    window.localStorage.setItem(languageBannerStorageKey, 'dismissed');
  } catch {
    // storage unavailable
  }
}